import { DealRowActionType as DealOperationActionType } from '@openx/components-deal/constants';
import { canArchiveDeal } from '@openx/components-deal/lib/utils/canArchiveDeal';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext';
import type { DealsRowsActionsType } from '@openx/components-deal/lib/utils/context/DealHookTypes';
import { useDealsOperations } from '@openx/components-deal/lib/utils/hooks/useDealsOperations';
import { OPENX_AGENT_TOOL } from '@openx/components-openx-agent/components/Home/utils/consts';
import { MenuItemIcon } from '@openx/components-openx-agent/components/icons/MenuItemIcon';
import { useOpenXAgentContext } from '@openx/components-openx-agent/context/useOpenXAgentContext';
import { ACL_TYPE } from '@openx/types/aclType';
import { type DealOaApi, type OaDealStatus, oaDealStatus } from '@openx/types/deal';
import type { ConstValues } from '@openx/types/helpers';
import type { RowActionProps } from '@openx/types/tableHelpers';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDealChangeStatusRestriction } from '../../../utils/useDealChangeStatusRestriction';
import { useIsAllowed } from '../../permission/utils/useIsAllowed';

export const DealRowActionType = {
  DETAILS: 'DETAILS',
  CHANGE_STATUS: 'CHANGE_STATUS',
  EDIT_DETAILS: 'EDIT_DETAILS',
  ARCHIVE: 'ARCHIVE',
  DOWNLOAD: 'DOWNLOAD',
  USE_AS_TEMPLATE: 'USE_AS_TEMPLATE',
  USE_SHARED_TARGETING: 'USE_SHARED_TARGETING',
  CHECK_TARGETING: 'CHECK_TARGETING',
} as const;
export type DealRowActionType = ConstValues<typeof DealRowActionType>;

type RowActionsType = Record<DealRowActionType, RowActionProps<DealOaApi>>;

export const useDealsRowActions: DealsRowsActionsType = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { isAllowed } = useIsAllowed();
  const { setIsOpenXAgentOpen, setActiveTool } = useOpenXAgentContext();

  const directDealNotAllowedMessage = t('This action is not available for direct deals.');

  const { useDealGroupFetch } = useDealContext();
  const [currentDeal, setCurrentDeal] = useState<DealOaApi | null>(null);

  const { callDealAction } = useDealsOperations();

  const restriction = useDealChangeStatusRestriction(currentDeal);

  const [rowLoading, setRowLoading] = useState<Record<string, boolean>>({});

  const { group, loading: dealGroupLoading } = useDealGroupFetch(currentDeal?.deal_id ?? '');
  const dealGroupWithRboDynamic = group.find(({ rboConfig }) => rboConfig?.tactic === 'dynamic');

  const isDealActive = useCallback((status: DealOaApi['status']) => {
    return status.toLowerCase() === oaDealStatus.ACTIVE.toLowerCase();
  }, []);

  const dataTest = useCallback(
    (status: DealOaApi['status']): string => {
      return isDealActive(status) ? 'deal-pause' : 'deal-reactivate';
    },
    [isDealActive]
  );

  const rowLoadingCallbacks = useCallback((deal: DealOaApi) => {
    const resetLoading = () => setRowLoading(prev => ({ ...prev, [deal.exchange_uid]: false }));
    return {
      onCancel: resetLoading,
      onFinish: resetLoading,
    };
  }, []);

  const setRowLoadingValue = useCallback((dealExchangeUid: string, value: boolean) => {
    setRowLoading(prev => ({ ...prev, [dealExchangeUid]: value }));
  }, []);

  const handleChangeStatus = useCallback(
    (deal: DealOaApi) => {
      setCurrentDeal(deal);
      setRowLoadingValue(deal.exchange_uid, true);
    },
    [setRowLoadingValue]
  );

  const callDealActionForStatusChange = useCallback(
    (deal: DealOaApi) => {
      const showRboAlert = !!dealGroupWithRboDynamic && group.length > 1;
      setRowLoadingValue(deal.exchange_uid, true);
      callDealAction({
        action: isDealActive(deal.status) ? DealOperationActionType.PAUSE : DealOperationActionType.RESUME,
        deals: [deal],
        skipModal: !isDealActive(deal.status) && !showRboAlert,
        showRBOAlert: showRboAlert,
        ...rowLoadingCallbacks(deal),
      });
    },
    [callDealAction, isDealActive, rowLoadingCallbacks, setRowLoadingValue, dealGroupWithRboDynamic, group.length]
  );

  useEffect(() => {
    if (!currentDeal) return;
    if (dealGroupLoading) return;

    if (restriction.loading) return;

    if (restriction.blocked) {
      setRowLoadingValue(currentDeal.exchange_uid, false);
      setCurrentDeal(null);
      enqueueSnackbar(restriction.reason, { variant: 'error' });
      return;
    }

    callDealActionForStatusChange(currentDeal);

    setCurrentDeal(null);
  }, [
    currentDeal,
    dealGroupLoading,
    restriction.loading,
    restriction.blocked,
    restriction.reason,
    callDealActionForStatusChange,
    enqueueSnackbar,
    setRowLoadingValue,
  ]);

  const handleUseDealAsTemplate = useCallback(
    (deal: DealOaApi) => {
      navigate('/deals/create', {
        state: {
          dealId: deal?.exchange_uid,
          useAsTemplate: true,
        },
      });
    },
    [navigate]
  );

  const handleUseSharedTargeting = useCallback(
    (deal: DealOaApi) => {
      navigate('/deals/create', {
        state: {
          dealId: deal?.exchange_uid,
          useSharedTargeting: true,
        },
      });
    },
    [navigate]
  );

  const handleDeleteDeal = useCallback(
    (deal: DealOaApi) => {
      setRowLoadingValue(deal.exchange_uid, true);
      callDealAction({
        action: DealOperationActionType.DELETE,
        deals: [{ ...deal, id: deal.exchange_uid }],
        ...rowLoadingCallbacks(deal),
      });
    },
    [callDealAction, setRowLoadingValue, rowLoadingCallbacks]
  );

  const actions: RowActionsType = useMemo(
    () => ({
      ARCHIVE: {
        allowed: deal => !deal.is_direct_deal && canArchiveDeal(deal.status as OaDealStatus),
        'data-test': 'deal-delete',
        hide: deal => !!deal.is_direct_deal,
        label: t('Delete Deal'),
        loading: ({ exchange_uid }) => rowLoading[exchange_uid],
        notAllowedMessage: deal =>
          deal.is_direct_deal
            ? directDealNotAllowedMessage
            : isDealActive(deal.status)
              ? t('Deal must be paused to Delete')
              : t('Cannot delete deal because the deal is in publishing state'),
        onClick: handleDeleteDeal,
      },
      CHANGE_STATUS: {
        allowed: deal =>
          !deal.is_direct_deal &&
          (isDealActive(deal.status)
            ? deal.status.toLowerCase() === oaDealStatus.ACTIVE.toLowerCase()
            : deal.status.toLowerCase() === oaDealStatus.PAUSED.toLowerCase()),
        danger: true,
        'data-test': ({ status }) => dataTest(status),
        hide: deal => !!deal.is_direct_deal,
        label: ({ status }) => (isDealActive(status) ? 'Pause Deal' : 'Reactivate Deal'),
        loading: ({ exchange_uid }) => rowLoading[exchange_uid],

        notAllowedMessage: deal =>
          deal.is_direct_deal
            ? directDealNotAllowedMessage
            : isDealActive(deal.status)
              ? t('Only active deals can be paused.')
              : t('Only paused deals can be reactivated.'),
        onClick: deal => handleChangeStatus(deal),
      },
      DETAILS: {
        'data-test': 'deal-details',
        label: t('Details'),
        onClick: ({ exchange_uid }) => navigate(`${exchange_uid}/details`),
      },
      DOWNLOAD: {
        allowed: deal => !deal.is_direct_deal,
        'data-test': 'deal-download',
        hide: deal => !!deal.is_direct_deal,
        label: t('Download'),
        notAllowedMessage: directDealNotAllowedMessage,
        onClick: deal => callDealAction({ action: DealOperationActionType.DOWNLOAD, deals: [deal] }),
      },
      EDIT_DETAILS: {
        allowed: deal => !deal.is_direct_deal,
        'data-test': 'deal-details-edit',
        hide: deal => !!deal.is_direct_deal,
        label: t('Edit Deal Details'),
        notAllowedMessage: directDealNotAllowedMessage,
        onClick: ({ exchange_uid }) => navigate(`${exchange_uid}/edit`),
      },
      TROUBLESHOOT: {
        allowed: deal => !deal.is_direct_deal && isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_READ)),
        'data-test': 'troubleshoot',
        hide: deal => !!deal.is_direct_deal,
        label: t('Troubleshoot'),
        notAllowedMessage: deal =>
          deal.is_direct_deal ? directDealNotAllowedMessage : t('You need permission to perform this action'),
        onClick: deal => {
          navigate(`/deals/${deal.uid}/troubleshoot`);
        },
      },
      USE_AS_TEMPLATE: {
        allowed: deal => !deal.is_direct_deal,
        'data-test': 'use-as-template',
        hide: deal => !!deal.is_direct_deal,
        label: t('Use As Template'),
        notAllowedMessage: directDealNotAllowedMessage,
        onClick: handleUseDealAsTemplate,
      },
      USE_SHARED_TARGETING: {
        allowed: deal => !deal.is_direct_deal,
        'data-test': 'use-shared-targeting',
        hide: deal => !!deal.is_direct_deal,
        label: t('Use Shared Targeting'),
        notAllowedMessage: directDealNotAllowedMessage,
        onClick: handleUseSharedTargeting,
      },
      CHECK_TARGETING: {
        'data-test': 'check-targeting',
        label: t('Check Targeting'),
        onClick: pckg => {
          setActiveTool({
            toolId: OPENX_AGENT_TOOL.TARGETING_EXPLAINER,
            toolData: pckg,
          });
          setIsOpenXAgentOpen(true);
        },
        startIcon: <MenuItemIcon />,
        allowed: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.XPERT_ENABLED)),
        hide: !isAllowed(isAllowedByCompiledAcl(ACL_TYPE.XPERT_ENABLED)),
      },
    }),
    [
      callDealAction,
      dataTest,
      directDealNotAllowedMessage,
      handleChangeStatus,
      handleDeleteDeal,
      handleUseDealAsTemplate,
      handleUseSharedTargeting,
      isDealActive,
      navigate,
      rowLoading,
      t,
      isAllowed,
      setIsOpenXAgentOpen,
      setActiveTool,
    ]
  );

  return actions;
};
